<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { helpers } from '@vuelidate/validators'
  import FlowCard from '@/shared/components/FlowCard/FlowCard.vue'
  import { useCommonStore, useUserStore } from '@/shared/store'
  import { usePerformanceTiming, useValidation, useValidators } from '@/shared/composables'
  import BaseEmailInput from '@/shared/components/BaseEmailInput/BaseEmailInput.vue'
  import BasePromocodeInput from '@/shared/components/BasePromocodeInput/BasePromocodeInput.vue'
  import BaseCheckbox from '@/shared/components/BaseCheckbox/BaseCheckbox.vue'
  import FlowPrivacyPolicyReminder from '@/entities/Flow/components/FlowPrivacyPolicyReminder'
  import { PhoneInput } from '@qic/ui-components'
  import {
    COUNTRY_CODE,
    LOAD_EVENT_NAME,
    PROCEED_EVENT_NAME,
    FLOW_CONTACTS_EMAIL_TEST_ID,
    FLOW_CONTACTS_PHONE_TEST_ID,
  } from './FlowContactsWidget.constants'
  import { TravelDirection } from '@/shared/types/common.types'
  import { useGoogleAnalytics } from '@/shared/composables/useGoogleAnalytics/useGoogleAnalytics'
  import { convertStartDateToTimestamp } from '@qicgroupdoha/travel-devkit/helpers/dates'
  import { useHomeUrl } from '@/shared/composables/useHomeUrl'
  import { TravelType } from '@/shared/types/api.types'

  interface FormData {
    phone: string
    email: string
    promocode: string
    contactsAgree: number
    numAdults?: number
    numChildren?: number
    policyDate?: number
    endDate?: number
    travelType?: string
  }

  interface Props {
    typeOfTravel?: Exclude<TravelDirection, TravelDirection.UNKNOWN>
  }

  interface Emits {
    (e: typeof PROCEED_EVENT_NAME): void
    (
      e: typeof LOAD_EVENT_NAME,
      request: () => Promise<any>,
      requestId?: string,
      withLoader?: boolean,
      withError?: boolean
    ): void
  }

  const props = withDefaults(defineProps<Props>(), {
    typeOfTravel: TravelDirection.OUTBOUND,
  })

  const emit = defineEmits<Emits>()
  const { getHomeUrl } = useHomeUrl()
  const homeUrl = getHomeUrl()
  const userStore = useUserStore()
  const commonStore = useCommonStore()
  const { emailValidator, phoneValidator } = useValidators()
  const gaService = useGoogleAnalytics()
  const { t } = useI18n()
  const { onPerformanceCheckEnd } = usePerformanceTiming()
  const loading = ref(true)

  const isInbound = props.typeOfTravel === TravelDirection.INBOUND
  const contactsTitle = isInbound ? t('inbound.contacts.title') : t('outbound.contacts.title')

  const policyUrl = ref('')
  const phone = ref('')
  const maskedPhone = ref('')
  const phoneCompleted = ref(false)
  const email = ref('')
  const emailErrorMessage = ref('')
  const promocode = ref(userStore.promocode ?? '')
  const isAgree = ref(true)
  const countryCode = ref(userStore.countryCode ?? COUNTRY_CODE)

  const resetErrorMessage = () => (emailErrorMessage.value = '')

  const validationRules = computed(() => ({
    email: {
      ...emailValidator,
      errorMessage: helpers.withMessage(
        t('qic.error.email.format'),
        () => !emailErrorMessage.value
      ),
    },
    phoneCompleted: {
      ...phoneValidator,
    },
  }))

  const { v$, errors } = useValidation(validationRules.value, { email, phoneCompleted })
  const errorsCustom = ref<string[]>([])

  const validateEmail = () =>
    commonStore.validateEmail(email.value).then((message) => {
      emailErrorMessage.value = message
      return message
    })

  const onProceed = async () => {
    v$.value.$touch()

    if (!v$.value.$error && !errorsCustom.value.length) {
      await emit(LOAD_EVENT_NAME, async () => {
        const message = await validateEmail()
        if (message) {
          return
        }

        userStore.setPromocode(promocode.value, true)
        userStore.setPhone(phone.value, true)
        userStore.setMaskedPhone(maskedPhone.value, true)
        userStore.setEmail(email.value, true)
        userStore.setCountryCode(countryCode.value, true)

        const data: FormData = {
          phone: phone.value,
          email: email.value,
          promocode: promocode.value,
          contactsAgree: isAgree.value ? 1 : 0,
        }

        const hasAllHeroSectionData =
          userStore.isHeroSection &&
          userStore.departureDate &&
          userStore.returnDate &&
          userStore.tripType &&
          userStore.adultsCount

        const hasNoHeroSectionData =
          userStore.isHeroSection &&
          (!userStore.departureDate ||
            !userStore.returnDate ||
            !userStore.tripType ||
            !userStore.adultsCount)

        if (hasAllHeroSectionData) {
          data.numAdults = userStore.adultsCount
          data.numChildren = userStore.childrenCount
          // @ts-ignore
          data.policyDate = convertStartDateToTimestamp(new Date(userStore.departureDate))
          // @ts-ignore
          data.endDate = convertStartDateToTimestamp(new Date(userStore.returnDate))
          data.travelType =
            userStore.tripType === 'SINGLE' ? TravelType.SINGLE : TravelType.MULTITRIP
        } else if (hasNoHeroSectionData) {
          return (window.location.href = homeUrl)
        }

        await commonStore.sendFormData(data)

        gaService.sendContactsProceed(
          props.typeOfTravel,
          promocode.value,
          isAgree.value,
          userStore.destination?.map((country) => country?.id || '')
        )

        emit(PROCEED_EVENT_NAME)
      })
    }
  }

  const onPromocodeValidation = (newPromocode: string) =>
    commonStore.validatePromocode(newPromocode, 'TRAVEL')

  const fetchPolicyUrl = async () => {
    try {
      policyUrl.value = await commonStore.getPrivacyPolicyUrl()
    } catch (error) {
      console.error(error)
    }
  }

  // const onPolicyClick = () => {
  //   // TODO: перейти на useFlowSteps после появления возможности переиспользования composable
  //   gaService.sendClickPrivacyPolicy(route.name as InboundFlowRoutes | OutboundFlowRoutes);
  // };
  onMounted(() => {
    emit(
      LOAD_EVENT_NAME,
      async () => {
        await Promise.all([userStore.getFormData(props.typeOfTravel, false), fetchPolicyUrl()])
        phone.value = userStore.phone ?? ''
        maskedPhone.value = userStore.maskedPhone ?? ''
        email.value = userStore.email ?? ''
        loading.value = false
        onPerformanceCheckEnd()
      },
      'contacts',
      true,
      false
    )
  })
</script>

<template>
  <FlowCard :title="contactsTitle" with-button class="flow-contacts" @button-click="onProceed">
    <div v-if="!loading" class="flow-contacts__content">
      <BaseEmailInput
        v-model="email"
        :errors="errors.email"
        big
        :data-test="FLOW_CONTACTS_EMAIL_TEST_ID"
        @update:model-value="resetErrorMessage"
      />
      <PhoneInput
        v-model="phone"
        v-model:masked-value="maskedPhone"
        v-model:completed="phoneCompleted"
        v-model:country-code="countryCode"
        :errors="errors.phoneCompleted"
        big
        :excluded-countries="['IL']"
        :data-test="FLOW_CONTACTS_PHONE_TEST_ID"
      />
      <BasePromocodeInput
        v-model="promocode"
        :is-shown="promocode.length > 0"
        :validation="onPromocodeValidation"
        data-test="contacts-promocode-input"
      />
      <div class="flow-contacts__agreed-wrapper">
        <BaseCheckbox
          id="agreed"
          v-model="isAgree"
          name="agreed"
          class="flow-contacts__agreed"
          data-test="contacts-agree-checkbox"
        >
          <p>{{ $t('qic_user_contacts_acceptance') }}</p>
        </BaseCheckbox>
      </div>
    </div>
    <template #after>
      <div class="flow-contacts__after-content">
        <FlowPrivacyPolicyReminder data-test="contacts-privacy-policy-reminder" />
      </div>
    </template>
  </FlowCard>
</template>

<style scoped lang="scss">
  @import './FlowContactsWidget';
</style>
